interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onPageChange: (offset: number) => void;
  /** Offer a page-size choice when given. */
  onLimitChange?: (limit: number) => void;
  pageSizes?: number[];
}

const btn =
  'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40';

/** "1–25 of 120" with previous/next and an optional page size. */
export function Pagination({ total, limit, offset, onPageChange, onLimitChange, pageSizes = [10, 25, 50, 100] }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(offset / limit) + 1;
  if (total === 0) return null;
  const from = offset + 1;
  const to = Math.min(offset + limit, total);

  return (
    <div className="mt-3 flex flex-col gap-3 border-t border-slate-100 px-1 pt-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-slate-500 tabular-nums" aria-live="polite">
        <span className="font-medium text-slate-700">
          {from}–{to}
        </span>{' '}
        of {total.toLocaleString()}
      </p>
      <div className="flex items-center gap-3">
        {onLimitChange && (
          <label className="flex items-center gap-2 text-sm text-slate-500">
            Rows
            <select
              aria-label="Rows per page"
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700"
            >
              {pageSizes.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button type="button" aria-label="Previous page" className={btn} onClick={() => onPageChange(Math.max(0, offset - limit))} disabled={offset <= 0}>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <span className="px-1 text-sm text-slate-600 tabular-nums">
              Page {currentPage} of {totalPages}
            </span>
            <button type="button" aria-label="Next page" className={btn} onClick={() => onPageChange(offset + limit)} disabled={offset + limit >= total}>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
